import React, { useEffect, useState } from "react";
import {
  Shield,
  CheckCircle,
  UserCheck,
  Database,
  Search,
  Lock,
} from "lucide-react";

const CibilCheck = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fadeIn");
            entry.target.classList.remove("opacity-0");
          }
        });
      },
      { threshold: 0.1 }
    );

    const hiddenElements = document.querySelectorAll(".initially-hidden");
    hiddenElements.forEach((el) => observer.observe(el));

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 5);
    }, 3000);

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
      clearInterval(interval);
    };
  }, []);

  const processSteps = [
    {
      title: "Instant Credit Score Retrieval",
      description: "Access the candidate’s CIBIL score and credit history.",
      icon: <Database className="w-8 h-8 text-red-300" />,
      image:
        "https://images.pexels.com/photos/259132/pexels-photo-259132.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1", // Calculator and finance
    },
    {
      title: "Loan & Credit Card History Check",
      description:
        "Identify overdue payments, defaults, and high debt burdens.",
      icon: <Search className="w-8 h-8 text-red-300" />,
      image:
        "https://images.pexels.com/photos/4968630/pexels-photo-4968630.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1", // Credit card and money
    },
    {
      title: "Fraud & Defaulter List Screening",
      description:
        "Cross-check with regulatory blacklists and defaulter databases.",
      icon: <Shield className="w-8 h-8 text-red-300" />,
      image:
        "https://images.pexels.com/photos/4386321/pexels-photo-4386321.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1", // Security and documents
    },
    {
      title: "Automated Risk Assessment",
      description:
        "AI-powered evaluation of financial behavior and risk indicators.",
      icon: <CheckCircle className="w-8 h-8 text-red-300" />,
      image:
        "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1", // Data analysis
    },
    {
      title: "Secure & Compliant Process",
      description: "100% digital, encrypted, and consent-based verification.",
      icon: <Lock className="w-8 h-8 text-red-300" />,
      image:
        "https://images.pexels.com/photos/705792/pexels-photo-705792.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1", // Digital security
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 to-gray-900 text-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/60 to-red-900/90 z-10"></div>

        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/6694543/pexels-photo-6694543.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Updated to financial analysis image
            alt="CIBIL Check Background"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-20 container mx-auto px-4 h-full flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="inline-block mb-4">
              <div className="bg-white/10 p-3 rounded-full animate-pulse">
                <Shield className="h-8 w-8 text-red-300" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fadeInDown">
              CIBIL Check
            </h1>
            <p className="text-xl text-red-200 mb-8 max-w-2xl animate-fadeInUp">
              Assess financial stability and mitigate risks with Criterion BGV’s
              detailed credit history verification.
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeInUp">
              <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center">
                <Lock className="w-5 h-5 mr-2" />
                Get Started
              </button>
              {/* <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
                Learn More
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 py-16">
        {/* <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Understanding CIBIL Check</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <p className="text-lg leading-relaxed mb-6">
                A candidate’s financial stability and creditworthiness are key indicators of reliability, especially in BFSI, finance, and sensitive roles.
              </p>
              <p className="text-lg leading-relaxed">
                Criterion BGV’s CIBIL Check provides a comprehensive credit history assessment to mitigate financial and reputational risks.
              </p>
              
              <div className="mt-8 bg-red-900/30 p-6 rounded-lg border border-red-500/30">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Shield className="w-6 h-6 text-red-300 mr-2" />
                  Why It’s Essential
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Financial Responsibility</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Fraud Prevention</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Regulatory Compliance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Risk Mitigation</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-full shadow-lg">
              <img 
                src="https://images.pexels.com/photos/3943723/pexels-photo-3943723.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Financial documents
                alt="CIBIL Check" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent flex items-end">
                <p className="p-4 text-white font-medium">Ensure financial integrity in hiring</p>
              </div>
            </div>
          </div>
        </div> */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">
            Understanding CIBIL Check
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <p className="text-lg leading-relaxed mb-6">
                A candidate’s financial stability and creditworthiness are key
                indicators of reliability, especially in BFSI, finance, and
                sensitive roles.
              </p>
              <p className="text-lg leading-relaxed">
                Criterion BGV’s CIBIL Check provides a comprehensive credit
                history assessment to mitigate financial and reputational risks.
              </p>

              <div className="mt-8 bg-red-900/30 p-6 rounded-lg border border-red-500/30">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Shield className="w-6 h-6 text-red-300 mr-2" />
                  Why It’s Essential
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Financial Responsibility</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Fraud Prevention</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Regulatory Compliance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Risk Mitigation</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-full shadow-lg">
              <img
                src="https://images.pexels.com/photos/164558/pexels-photo-164558.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Updated to piggy bank image
                alt="CIBIL Check"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent flex items-end">
                <p className="p-4 text-white font-medium">
                  Ensure financial integrity in hiring
                </p>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .animate-fadeIn {
            animation: fadeIn 1s ease forwards;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}</style>

        {/* Verification Process Animation */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">
            Our CIBIL Verification Process
          </h2>

          <div className="relative">
            <div className="hidden md:block absolute top-0 left-1/2 w-1 bg-red-500/50 h-full transform -translate-x-1/2 z-0"></div>

            {processSteps.map((step, index) => (
              <div
                key={index}
                className={`relative z-10 transition-all duration-500 mb-8 ${
                  activeStep === index ? "scale-100" : "scale-95 opacity-70"
                }`}
              >
                <div className="flex flex-col md:flex-row items-center">
                  <div
                    className={`w-full md:w-1/2 ${
                      index % 2 === 0
                        ? "md:pr-12 md:text-right"
                        : "md:order-2 md:pl-12"
                    }`}
                  >
                    <div
                      className={`bg-white/5 p-6 rounded-lg border border-white/10 transition-all duration-300 ${
                        activeStep === index
                          ? "bg-white/15 border-red-500/50 shadow-lg"
                          : ""
                      }`}
                    >
                      <div className="flex items-center mb-4 justify-center">
                        <div
                          className={`p-3 rounded-full ${
                            activeStep === index
                              ? "bg-red-600/30"
                              : "bg-white/10"
                          }`}
                        >
                          {step.icon}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-red-300 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-white/80">{step.description}</p>
                    </div>
                  </div>

                  <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center justify-center">
                    <div
                      className={`w-10 h-10 rounded-full border-4 z-20 flex items-center justify-center transition-all duration-300 ${
                        activeStep === index
                          ? "bg-red-600 border-red-900 scale-125"
                          : "bg-red-900/70 border-red-700/50"
                      }`}
                    >
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                  </div>

                  <div
                    className={`w-full md:w-1/2 mt-4 md:mt-0 ${
                      index % 2 === 0 ? "md:order-2 md:pl-12" : "md:pr-12"
                    }`}
                  >
                    <div
                      className={`h-48 rounded-lg overflow-hidden transition-all duration-300 ${
                        activeStep === index
                          ? "shadow-2xl ring-2 ring-red-500/50"
                          : ""
                      }`}
                    >
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Benefits */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">
            Key Benefits of CIBIL Check
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Operational Benefits */}
            <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <Database className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Operational Benefits</h3>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">
                        Faster Turnaround Time
                      </h4>
                      <p className="text-white/80">
                        Instant access to financial records.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Paperless & Secure</h4>
                      <p className="text-white/80">
                        Real-time access to credit history with complete data
                        privacy.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Cost Savings</h4>
                      <p className="text-white/80">
                        Reduce fraud risks and potential legal liabilities.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-red-900/30 p-4 rounded-lg">
                  <h4 className="font-bold mb-2">Advantages:</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Speed</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Efficiency</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Cost-Effectiveness</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Security</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="h-48 relative">
                <img
                  src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Data analysis
                  alt="Operational Benefits"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">
                    Streamline hiring with instant credit checks
                  </p>
                </div>
              </div>
            </div>

            {/* Security Benefits */}
            <div className="bg-gradient-to-br from-red-900/20 to-red-700/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <UserCheck className="h-8 w-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Security Benefits</h3>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">
                        Enhanced Hiring Decisions
                      </h4>
                      <p className="text-white/80">
                        Ensure candidates have a stable financial background.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Fraud Prevention</h4>
                      <p className="text-white/80">
                        Identify financial misconduct and defaults.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1">
                      <CheckCircle className="h-4 w-4 text-red-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">
                        Regulatory Compliance
                      </h4>
                      <p className="text-white/80">
                        Adhere to BFSI financial integrity guidelines.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-red-900/30 p-4 rounded-lg">
                  <h4 className="font-bold mb-2">Advantages:</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Risk Reduction</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Reliability</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Trust</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-red-300 mr-2">✓</span>
                      <span>Compliance</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="h-48 relative">
                <img
                  src="https://images.pexels.com/photos/705792/pexels-photo-705792.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" // Digital security
                  alt="Security Benefits"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">
                    Secure hiring with financial vetting
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">
            Best Practices for CIBIL Check
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-red-800/30 to-red-600/30 p-6 rounded-xl border border-red-500/20">
              <h3 className="text-2xl font-bold mb-6 text-center">
                For Organizations
              </h3>

              <div className="space-y-4">
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Obtain candidate consent</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Use AI for risk assessment</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Screen for fraud lists</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Ensure data encryption</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-800/30 to-red-600/30 p-6 rounded-xl border border-red-500/20">
              <h3 className="text-2xl font-bold mb-6 text-center">
                For HR Teams
              </h3>

              <div className="space-y-4">
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Review credit history reports</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Assess financial stability</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Verify compliance adherence</p>
                </div>

                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Act on high-risk findings</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16 initially-hidden opacity-0">
          {[
            {
              number: "Instant",
              text: "access to credit scores",
              color: "from-red-600/20 to-red-400/20",
              border: "border-red-500/30",
            },
            {
              number: "100%",
              text: "digital and secure process",
              color: "from-pink-600/20 to-pink-400/20",
              border: "border-pink-500/30",
            },
            {
              number: "AI-Powered",
              text: "risk assessment",
              color: "from-orange-600/20 to-orange-400/20",
              border: "border-orange-500/30",
            },
            {
              number: "Real-Time",
              text: "credit history access",
              color: "from-yellow-600/20 to-yellow-400/20",
              border: "border-yellow-500/30",
            },
          ].map((stat, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${stat.color} rounded-xl p-6 shadow-xl ${stat.border} text-center`}
            >
              <div className="text-4xl font-bold text-white mb-2">
                {stat.number}
              </div>
              <p className="text-white/80">{stat.text}</p>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-red-600/30 to-pink-600/30 rounded-2xl p-8 shadow-2xl border border-red-500/20 text-center initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-4">
            Ensure Financial Integrity Today!
          </h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Make risk-free hiring decisions with Criterion’s real-time CIBIL
            Check.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center">
              <Lock className="w-5 h-5 mr-2" />
              Start Now
            </button>
            <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
              Contact Us
            </button>
          </div>
        </div>
      </section>

      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 1s ease forwards;
        }

        .animate-fadeInDown {
          animation: fadeInDown 1s ease forwards;
        }

        .animate-fadeInUp {
          animation: fadeInUp 1s ease forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CibilCheck;
